import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { posthog, isPostHogConfigured } from "../lib/posthog";
import {
  LinkList,
  LinkDetail,
  AddLinkFAB,
  AddButton,
  InstallPrompt,
  InstallButton,
  AppHeader,
  GreetingSection,
  FiltersSection,
  MainContent,
  type LinkItemData,
  type FilterPeriod,
} from "../components";
import {
  useMinLoadingTime,
  useAuth,
  useInstallPrompt,
  useAppModals,
  useLinkActions,
  useTheme,
  useDrawer,
  useMediaQuery,
} from "../hooks";
import { useGetLinks, useGetLinksSearch } from "../api/generated/links/links";
import { listContextGroups } from "../api/services";
import styles from "./LinksScreen.module.css";

const transformLink = (link: {
  id?: string;
  url?: string;
  title?: string | null;
  customTitle?: string | null;
  tags?: string[];
  description?: string | null;
  image?: string | null;
  isFavorite?: boolean;
  createdAt?: string;
}): LinkItemData => ({
  id: link.id || "",
  url: link.url || "",
  title: link.title ?? null,
  customTitle: link.customTitle ?? null,
  tags: link.tags ?? [],
  description: link.description ?? null,
  image: link.image ?? null,
  isFavorite: link.isFavorite || false,
  createdAt: link.createdAt || new Date().toISOString(),
});

export const LinksScreen = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [filterPeriod, setFilterPeriod] = useState<FilterPeriod>("all");
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [selectedLink, setSelectedLink] = useState<LinkItemData | null>(null);

  useTheme();
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const { isAuthenticated, isInitialized, email, fullName, avatarUrl } = useAuth();

  const groupsQuery = useQuery({
    queryKey: ["groups", isAuthenticated],
    queryFn: async () => {
      const res = await listContextGroups();
      return res.data ?? [];
    },
    enabled: isInitialized,
  });
  const groups = groupsQuery.data ?? [];

  const { shouldShowPrompt, isIOS, promptInstall, dismiss } = useInstallPrompt();
  const {
    openAddLinkModal,
    openAuthPrompt,
    isInstallPromptOpen,
    openInstallPrompt,
    closeInstallPrompt,
  } = useAppModals();
  const { openDrawer } = useDrawer();

  const {
    createLinkMutation,
    deleteLinkMutation,
    updateLinkMutation,
    handleFavoriteToggle,
    handleDeleteLink,
    handleUpdateLink,
  } = useLinkActions({
    onLinkDeleted: () => setSelectedLink(null),
  });

  const handleUpdateLinkWithLocalState = async (
    id: string,
    data: { customTitle?: string | null; tags?: string[] }
  ) => {
    if (selectedLink && selectedLink.id === id) {
      setSelectedLink({
        ...selectedLink,
        ...(data.customTitle !== undefined && { customTitle: data.customTitle }),
        ...(data.tags !== undefined && { tags: data.tags }),
      });
    }
    await handleUpdateLink(id, data);
  };

  const handleFavoriteToggleWithLocalState = (link: LinkItemData) => {
    if (selectedLink && selectedLink.id === link.id) {
      setSelectedLink({ ...selectedLink, isFavorite: !selectedLink.isFavorite });
    }
    handleFavoriteToggle(link);
  };

  const isSearching = search.length >= 2;
  const timezoneOffset = new Date().getTimezoneOffset();
  const listQueryParams: Record<string, string> = {};
  if (filterPeriod !== "all") {
    listQueryParams.period = filterPeriod;
    listQueryParams.timezoneOffset = String(timezoneOffset);
  }
  if (selectedGroupId) listQueryParams.contextGroupId = selectedGroupId;

  const searchQueryParams: Record<string, string> = { q: search };
  if (filterPeriod !== "all") {
    searchQueryParams.period = filterPeriod;
    searchQueryParams.timezoneOffset = String(timezoneOffset);
  }
  if (selectedGroupId) searchQueryParams.contextGroupId = selectedGroupId;

  const linksQuery = useGetLinks(Object.keys(listQueryParams).length ? listQueryParams : undefined, { query: { enabled: !isSearching && isInitialized } });
  const searchQuery = useGetLinksSearch(searchQueryParams as { q: string }, { query: { enabled: isSearching && isInitialized } });

  useEffect(() => {
    if (!isSearching && linksQuery.isSuccess && linksQuery.data && isPostHogConfigured()) {
      posthog.capture("links_loaded", { count: linksQuery.data.data?.length || 0, period: filterPeriod });
    }
  }, [linksQuery.isSuccess, linksQuery.data, filterPeriod, isSearching]);

  const activeQuery = isSearching ? searchQuery : linksQuery;
  const links: LinkItemData[] = (activeQuery.data?.data || []).map(transformLink);
  const isLoading = useMinLoadingTime(activeQuery.isLoading, 400);
  const isRefetching = createLinkMutation.isPending || (activeQuery.isFetching && !activeQuery.isLoading);
  const error = activeQuery.error ? "Failed to load links" : null;

  const handleAuthClick = () => {
    if (isAuthenticated) {
      openDrawer();
    } else {
      openAuthPrompt();
    }
  };

  const handleInstall = async () => {
    const installed = await promptInstall();
    if (installed) closeInstallPrompt();
  };

  const userName = fullName ? fullName.split(" ")[0] : email?.split("@")[0];

  return (
    <>
      <div className={styles.stickyHeader}>
        <AppHeader
          isAuthenticated={isAuthenticated}
          fullName={fullName}
          email={email}
          avatarUrl={avatarUrl}
          onAuthClick={handleAuthClick}
          hideAuthOnDesktop={isDesktop}
        />
        <GreetingSection userName={userName} show={isAuthenticated && !search} />
        <FiltersSection
        search={search}
        filterPeriod={filterPeriod}
        selectedGroupId={selectedGroupId}
        groups={groups}
        onSearchChange={(e) => setSearch(e.target.value)}
        onSearchClear={() => setSearch("")}
        onFilterChange={setFilterPeriod}
        onGroupChange={setSelectedGroupId}
        searchAction={isDesktop ? <AddButton onClick={() => openAddLinkModal()} label={t("addLink.title", "Add link")} /> : undefined}
      />
      </div>
      <div className={styles.mainArea}>
        <div className={styles.listColumn}>
          <MainContent>
            <LinkList
              links={links}
              isLoading={isLoading}
              isRefetching={isRefetching}
              error={error}
              onLinkClick={(link) => window.open(link.url, "_blank")}
              onFavoriteToggle={handleFavoriteToggle}
              onMenuClick={setSelectedLink}
              emptyTitle={isSearching ? t("links.noLinksFound") : t("links.noLinksYet")}
              emptyDescription={isSearching ? t("links.tryDifferentSearch") : t("links.tapToSaveFirst")}
            />
          </MainContent>
        </div>
        {isDesktop && selectedLink && (
          <div className={styles.detailColumn}>
            <LinkDetail
              link={selectedLink}
              onClose={() => setSelectedLink(null)}
              onOpenLink={() => window.open(selectedLink.url, "_blank")}
              onDelete={() => handleDeleteLink(selectedLink.id)}
              onFavoriteToggle={() => handleFavoriteToggleWithLocalState(selectedLink)}
              onUpdate={handleUpdateLinkWithLocalState}
              isDeleting={deleteLinkMutation.isPending}
              isUpdating={updateLinkMutation.isPending}
              variant="panel"
            />
          </div>
        )}
      </div>
      {!isDesktop && <AddLinkFAB onClick={() => openAddLinkModal()} />}
      {shouldShowPrompt && <InstallButton onClick={openInstallPrompt} />}
      <InstallPrompt isOpen={isInstallPromptOpen} isIOS={isIOS} onInstall={handleInstall} onDismiss={() => { dismiss(); closeInstallPrompt(); }} />
      {!isDesktop && selectedLink && (
        <LinkDetail
          link={selectedLink}
          onClose={() => setSelectedLink(null)}
          onOpenLink={() => window.open(selectedLink.url, "_blank")}
          onDelete={() => handleDeleteLink(selectedLink.id)}
          onFavoriteToggle={() => handleFavoriteToggleWithLocalState(selectedLink)}
          onUpdate={handleUpdateLinkWithLocalState}
          isDeleting={deleteLinkMutation.isPending}
          isUpdating={updateLinkMutation.isPending}
          variant="modal"
        />
      )}
    </>
  );
};
